import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';

import { AUTH_SERVICE } from '@core/services/auth/auth.token';
import { AccessDeniedPath } from '@core/constants';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private authService = inject(AUTH_SERVICE);
  private messageService = inject(MessageService);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          // Navigate only after logout settles — a same-tick redirect aborts the request, so the
          // session survives server-side and a hard refresh silently signs the user back in.
          // finalize runs on completion or error, so the redirect is reliable either way.
          this.authService
            .logout()
            .pipe(
              finalize(() =>
                this.router.navigate([AccessDeniedPath], {
                  queryParams: { logout: true }
                })
              )
            )
            .subscribe();
          throw { error: err, message: 'Invalid credentials.' };
        } else if (err.status === 403) {
          const message: string =
            err.error?.message ??
            'You do not have permission to perform this action.';
          this.messageService.add({
            severity: 'error',
            summary: 'Access Denied',
            detail: message
          });
          throw err;
        } else {
          const error = err.message || err.statusText;
          console.log(error);
          Sentry.captureException(error);
          throw err;
        }
      })
    );
  }
}
